import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { paste } from "@testing-library/user-event/dist/paste";
// load these plugins globally
dayjs.extend(utc);
dayjs.extend(timezone);

export function getUsedPorts(devices) {
  if (!devices) return [];
  return Object.values(devices).map((obj) => obj.address);
}

export const asKw = (val) => {
  return Number(Math.round(val) / 1000).toFixed(1);
};

export const fmtAsKw = (val) => {
  return String(asKw(val)) + "kW";
};

export const asKwUnsigned = (val) => {
  return Number(Math.abs(Math.round(val)) / 1000).toFixed(1);
};

export const asWatt = (val) => {
  return Number(Math.abs(Math.round(val)));
};

export function groupBy(list_of_objs, key) {
  if (!list_of_objs) return {};
  const red = list_of_objs.reduce(function (accumulator, alert) {
    (accumulator[alert[key]] = accumulator[alert[key]] || []).push(alert);
    return accumulator;
  }, {});
  return red;
}

export function validateValue(value) {
  const parsed = parseInt(value);

  if (isNaN(parsed)) return null;
  return parsed;
}

export function connectionCat(ping) {
  if (ping === "nan") return 1;
  if (new Date().getTime() - 1000 * ping > 1000 * 60 * 60 * 24 * 2)
    return -1; // longer than two days offline
  else return new Date().getTime() - 1000 * ping < 1000 * 60 * 3 ? 0 : 1; // 3 mins
}

export function processValue(value, isString) {
  if (isString) return value.toString();
  else if (typeof value == "number" || typeof value == "boolean") return value;
  else if (!isString && value == "") return value;
  else return value.length > 0 ? parseInt(value) : 0;
}

export function phasedLimit(limit, connectionType = 3) {
  if (connectionType === 3) {
    return limit / 3;
  } else {
    return limit;
  }
}

export function formatCount(type, count) {
  if (count > 1) {
    type = type + "s";
  }
  return count + " " + type;
}

export function getConnectionStatusErrorLine(status, short) {
  if (status === 0) {
    return "Connection status unknown";
  } else if (status === 1) {
    return "Connected";
  } else if (status === 10) {
    return "Unknown connection error";
  } else if (status === 11) {
    return "Connection refused (502)" + (short ? "" : ": is Modbus TCP enabled?");
  } else if (status === 12) {
    return "Connection timeout" + (short ? "" : ": check IP address");
  }
}

export function getModbusStatusErrorLine(status, short) {
  if (status === 0) {
    return "Speed status unknown";
  } else if (status < 3) {
    return "Speed OK";
  } else if (status === 3) {
    return "Modbus transfer speed slow";
  } else if (status === 4) {
    return "Modbus transfer speed very slow";
  } else if (status === 10) {
    return "Unknown modbus speed error";
  }
}

export function getModbusQualityStatusErrorLine(status, short) {
  if (status === 0) {
    return "Quality status unknown";
  } else if (status < 3) {
    return "Quality OK";
  } else if (status === 3) {
    return "Modbus quality low";
  } else if (status === 4) {
    return "Modbus quality very low";
  } else if (status >= 10) {
    return "Unknown modbus quality error";
  }
}

export function BrusselsDateTimeStringToDate(dateString) {
  if (!dateString) return null;
  return dayjs.tz(dateString, "Europe/Brussels").toDate();
}

export function toLocaleString(d, showSeconds = false) {
  if (showSeconds) {
    return d ? new Date(d * 1000).toLocaleString() : "";
  } else {
    return d
      ? new Date(d * 1000).toLocaleString(undefined, {
          hour: "2-digit",
          minute: "2-digit",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        })
      : "";
  }
}

export function toLocaleDateString(d) {
  return d ? new Date(d * 1000).toLocaleDateString() : "";
}

export function toDate(d) {
  return d ? new Date(d * 1000) : null;
}

export function formatDate(d) {
  return (
    ("0" + d.getDate()).slice(-2) +
    "-" +
    ("0" + (d.getMonth() + 1)).slice(-2) +
    "-" +
    d.getFullYear() +
    " " +
    formatTime(d)
  );
}

export function formatTime(d) {
  return (
    ("0" + d.getHours()).slice(-2) + ":" + ("0" + d.getMinutes()).slice(-2) + ":" + ("0" + d.getSeconds()).slice(-2)
  );
}

export function verbalDate(timestamp) {
  const now = Date.now();
  const diffInSeconds = Math.floor((now - timestamp) / 1000);

  if (diffInSeconds < 5) {
    return "less than 5 seconds ago";
  } else if (diffInSeconds < 30) {
    return "less than 30 seconds ago";
  } else if (diffInSeconds < 60) {
    return "less than a minute ago";
  } else if (diffInSeconds < 5 * 60) {
    return "less than 5 minutes ago";
  } else if (diffInSeconds < 30 * 60) {
    return "less than 30 minutes ago";
  } else if (diffInSeconds < 60 * 60) {
    return "less than an hour ago";
  } else if (diffInSeconds < 24 * 60 * 60) {
    return "less than a day ago";
  } else if (diffInSeconds < 7 * 24 * 60 * 60) {
    return "less than a week ago";
  } else if (diffInSeconds < 30 * 24 * 60 * 60) {
    return "less than a month ago";
  } else if (diffInSeconds < 365 * 24 * 60 * 60) {
    return "less than a year ago";
  } else {
    return "over a year ago";
  }
}

export function timeAgo(date) {
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  const timeIntervals = [
    { unit: "year", seconds: 31536000 },
    { unit: "month", seconds: 2592000 },
    { unit: "week", seconds: 604800 },
    { unit: "day", seconds: 86400 },
    { unit: "hour", seconds: 3600 },
    { unit: "minute", seconds: 60 },
    { unit: "second", seconds: 1 },
  ];

  for (const interval of timeIntervals) {
    const difference = Math.floor(diffInSeconds / interval.seconds);
    if (difference >= 1) {
      return rtf.format(-difference, interval.unit);
    }
  }

  return "Just now"; // If within a few seconds
}

export function formatVolume(valueMW) {
  if (valueMW === null || valueMW === undefined) {
    return { value: "N/A", unit: "MW", scalar: null };
  } else if (valueMW < 1) {
    return { value: (1000 * valueMW).toFixed(0), unit: "kW", scalar: 1000 };
  }
  return { value: valueMW.toFixed(1), unit: "MW", scalar: 1 };
}

export function formatPrice(value) {
  if (value === null) {
    return "N/A";
  } else if (value < 1000) {
    return value.toFixed(0);
  } else if (value < 1000000) {
    return (value / 1000).toFixed(value < 10000 ? 1 : 0) + "k";
  }
  return (value / 1000000).toFixed(1) + "M";
}

function fnv1aHash(str) {
  let hash = 2166136261; // FNV-1a 32-bit offset basis
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  return hash >>> 0; // Convert to unsigned 32-bit integer
}

const HUE_RANGE = [{ start: 210, end: 360 }];
function warpHue(hue) {
  // Compute total available range length
  let totalLength = HUE_RANGE.reduce((sum, range) => sum + (range.end - range.start), 0);

  // Scale value to total length
  let scaledValue = hue * totalLength;

  // Find corresponding range
  for (let range of HUE_RANGE) {
    let rangeSize = range.end - range.start;
    if (scaledValue <= rangeSize) {
      return range.start + scaledValue;
    }
    scaledValue -= rangeSize; // Reduce by current range size and continue
  }
}

export function pastelColor(str) {
  // Step 2: Normalize the hash to a number between 0 and 1
  const normalizedHash = fnv1aHash(str + "apqddve123121q") / 4294967296;
  const hue = warpHue(normalizedHash);

  return "hsl(" + hue + "," + "70%," + "70%)";
}

export function ElindusRedColors(str) {
  // Step 2: Normalize the hash to a number between 0 and 1
  const normalizedHash = fnv1aHash(str + "apqddve123121q") / 4294967296;

  const value = 35.1 + 50 * normalizedHash;

  return "hsl(351, 100%, " + value + "%)";
}

export function shortenUsername(username) {
  if (!username) {
    return "";
  }

  const sp = username.split(" ");
  if (sp.length === 0) {
    return "";
  } else if (sp.length === 1) {
    return `${username[0]}`.toUpperCase();
  } else {
    return `${sp[0][0]}${sp[1][0]}`.toUpperCase();
  }
}

export function formatRecentDate(date) {
  if (date == null) return "N/A";

  const now = new Date();
  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);

  const optionsDate = { year: "numeric", month: "long", day: "numeric" };
  const optionsTime = { hour: "2-digit", minute: "2-digit", hour12: false };

  const localDate = new Date(date);
  const dateString = localDate.toDateString();
  const nowString = now.toDateString();
  const yesterdayString = yesterday.toDateString();

  if (dateString === nowString) {
    return `Today, ${localDate.toLocaleTimeString("en-GB", optionsTime)}`;
  } else if (dateString === yesterdayString) {
    return `Yesterday, ${localDate.toLocaleTimeString("en-GB", optionsTime)}`;
  } else {
    return date.toLocaleDateString("en-GB", optionsDate);
  }
}

export function formatEnergy(value) {
  if (value === null || value === undefined) return "-";
  return value >= 1000 ? (value / 1000).toFixed(2) + " MWh" : value + " kWh";
}

export function isBlockedForCommissioning(siteData) {
  if (siteData === null || siteData === undefined || siteData.has_devices_in_commissioning) return true;
  return false;
}

export function isSiteOffline(siteData) {
  return !siteData || !siteData.is_active;
}

export function expandProfitChart(timestamps, profits) {
  if (timestamps.length === 0) return [[], [], []];

  const lastTimestamp = new Date(timestamps[timestamps.length - 1]);
  const now = new Date();
  now.setSeconds(0, 0);
  now.setMinutes(now.getMinutes() - (now.getMinutes() % 15));
  if (
    lastTimestamp.getDate() !== now.getDate() ||
    lastTimestamp.getMonth() !== now.getMonth() ||
    lastTimestamp.getFullYear() !== now.getFullYear()
  )
    return [timestamps, profits, Array(profits.length).fill(false)];

  const result_timestamps = [...timestamps];
  const result_profits = [...profits];
  const result_flags = Array(profits.length).fill(false);

  let next = new Date(lastTimestamp.getTime() + 15 * 60 * 1000);

  while (next <= now) {
    result_timestamps.push(new Date(next));
    result_profits.push(profits[profits.length - 1]);
    result_flags.push(true);
    next = new Date(next.getTime() + 15 * 60 * 1000);
  }

  return [result_timestamps, result_profits, result_flags];
}
